import React, { useState, useEffect } from 'react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CurrencyInput from '@/components/CurrencyInput';
import WorkflowStepper from '@/components/WorkflowStepper';
import { Trash, Plus } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const CATEGORIES = [
  'Housing', 'Transportation', 'Food', 'Utilities', 'Healthcare',
  'Credit Cards', 'Debt Payments', 'Software/Subscriptions',
  'Entertainment', 'Shopping', 'Personal', 'Education', 'Other'
];

const SpendingTracker = () => {
  useTrackToolUsage('spending_tracker');
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    amount: null,
    category: '',
    description: '',
    is_need: true
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.amount === null || formData.amount === undefined || Number.isNaN(formData.amount)) {
      toast.error('Please enter an amount — commas and $ are okay');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    try {
      await api.post('/expenses', {
        ...formData,
        amount: Number(formData.amount)
      });
      toast.success('Expense added successfully');
      setFormData({ amount: null, category: '', description: '', is_need: true });
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const categoryData = CATEGORIES.map(cat => ({
    name: cat,
    value: expenses.filter(e => e.category === cat).reduce((sum, e) => sum + e.amount, 0)
  })).filter(c => c.value > 0);

  const COLORS = ['#0A1128', '#C5A059', '#4A5568', '#8B9BB4', '#2D6A4F', '#B7791F', '#C53030', '#E2E8F0'];

  const totalNeeds = expenses.filter(e => e.is_need).reduce((sum, e) => sum + e.amount, 0);
  const totalWants = expenses.filter(e => !e.is_need).reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-8 fade-in">
      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Spending Tracker
        </h1>
        <p className="text-base text-[#4A5568]">
          Track and categorize your expenses
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Expense Form */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="add-expense-form">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Add Expense</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="amount">Amount ($)</Label>
                <CurrencyInput
                  testid="expense-amount-input"
                  value={formData.amount}
                  onValue={(v) => setFormData({ ...formData, amount: v })}
                  placeholder="$0"
                  required
                  decimals
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger data-testid="expense-category-select" className="border-slate-300">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  data-testid="expense-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-slate-300"
                  required
                />
              </div>

              <div>
                <Label htmlFor="type">Type</Label>
                <Select value={formData.is_need ? 'need' : 'want'} onValueChange={(value) => setFormData({ ...formData, is_need: value === 'need' })}>
                  <SelectTrigger data-testid="expense-type-select" className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="need">Need</SelectItem>
                    <SelectItem value="want">Want</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" data-testid="add-expense-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
                <Plus size={18} className="mr-2" />
                Add Expense
              </Button>
            </form>
          </div>
        </div>

        {/* Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Needs vs Wants */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="needs-wants-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Needs vs Wants</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-[#2D6A4F]/10 p-4 rounded-md">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Needs</p>
                <p className="text-2xl font-semibold text-[#2D6A4F] ibm-plex">${totalNeeds.toLocaleString()}</p>
              </div>
              <div className="bg-[#C5A059]/10 p-4 rounded-md">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Wants</p>
                <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${totalWants.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="category-breakdown-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Spending by Category</h2>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-[#4A5568] py-8">No expenses to display</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="recent-expenses-list">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Recent Expenses</h2>
        {expenses.length === 0 ? (
          <p className="text-center text-[#4A5568] py-8">No expenses yet. Add your first expense above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Description</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Category</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Type</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Amount</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Action</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-slate-100" data-testid={`expense-row-${expense.id}`}>
                    <td className="py-3 px-4 text-sm text-[#1A202C]">{new Date(expense.date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-[#1A202C]">{expense.description}</td>
                    <td className="py-3 px-4 text-sm text-[#1A202C]">{expense.category}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        expense.is_need ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#C5A059]/10 text-[#B7791F]'
                      }`}>
                        {expense.is_need ? 'Need' : 'Want'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-[#1A202C] ibm-plex">${expense.amount.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleDelete(expense.id)}
                        data-testid={`delete-expense-${expense.id}`}
                        className="text-[#C53030] hover:text-[#9B2C2C] transition-colors"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WorkflowStepper testid="spending-workflow-stepper" />
    </div>
  );
};

export default SpendingTracker;